import { useState, useEffect, useRef } from 'react';

const API = 'https://text-reminder-backend-production.up.railway.app';
const TWILIO_PHONE = '+15073535947';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];
const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

const DAYS_OF_WEEK = [
  { label: 'Sunday',    value: 0 },
  { label: 'Monday',    value: 1 },
  { label: 'Tuesday',   value: 2 },
  { label: 'Wednesday', value: 3 },
  { label: 'Thursday',  value: 4 },
  { label: 'Friday',    value: 5 },
  { label: 'Saturday',  value: 6 },
];

const DAYS_OF_MONTH = [
  ...Array.from({ length: 31 }, (_, i) => ({ label: ordinal(i + 1), value: i + 1 })),
  { label: 'Last day of month', value: -1 },
];

function ordinal(n) {
  const s = ['th','st','nd','rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

const EVENT_TIMES = [
  '6:00 AM', '6:30 AM', '7:00 AM', '7:30 AM', '8:00 AM', '8:30 AM',
  '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
  '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM',
  '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM', '5:00 PM', '5:30 PM',
  '6:00 PM', '6:30 PM', '7:00 PM', '7:30 PM', '8:00 PM', '8:30 PM',
  '9:00 PM', '9:30 PM', '10:00 PM',
];

const TIMEZONES = [
  { label: 'US & Canada', group: true },
  { value: 'America/New_York',    label: 'Eastern Time (ET)' },
  { value: 'America/Chicago',     label: 'Central Time (CT)' },
  { value: 'America/Denver',      label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage',   label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu',    label: 'Hawaii Time (HT)' },
  { label: 'Europe', group: true },
  { value: 'Europe/London',       label: 'London (GMT/BST)' },
  { value: 'Europe/Paris',        label: 'Central Europe (CET)' },
  { value: 'Europe/Athens',       label: 'Eastern Europe (EET)' },
  { label: 'Asia & Pacific', group: true },
  { value: 'Asia/Dubai',          label: 'Gulf Time (GST)' },
  { value: 'Asia/Kolkata',        label: 'India (IST)' },
  { value: 'Asia/Singapore',      label: 'Singapore (SGT)' },
  { value: 'Asia/Tokyo',          label: 'Japan (JST)' },
  { value: 'Australia/Sydney',    label: 'Sydney (AEDT)' },
  { label: 'Latin America', group: true },
  { value: 'America/Sao_Paulo',   label: 'Brazil (BRT)' },
  { value: 'America/Mexico_City', label: 'Mexico City (CST)' },
  { value: 'America/Argentina/Buenos_Aires', label: 'Argentina (ART)' },
];

const COUNTRY_CODES = [
  { flag: '🇺🇸', name: 'United States', code: '+1' },
  { flag: '🇨🇦', name: 'Canada', code: '+1' },
  { flag: '🇬🇧', name: 'United Kingdom', code: '+44' },
  { flag: '🇦🇺', name: 'Australia', code: '+61' },
  { flag: '🇩🇪', name: 'Germany', code: '+49' },
  { flag: '🇫🇷', name: 'France', code: '+33' },
  { flag: '🇮🇹', name: 'Italy', code: '+39' },
  { flag: '🇪🇸', name: 'Spain', code: '+34' },
  { flag: '🇵🇹', name: 'Portugal', code: '+351' },
  { flag: '🇲🇽', name: 'Mexico', code: '+52' },
  { flag: '🇧🇷', name: 'Brazil', code: '+55' },
  { flag: '🇮🇳', name: 'India', code: '+91' },
  { flag: '🇯🇵', name: 'Japan', code: '+81' },
  { flag: '🇰🇷', name: 'South Korea', code: '+82' },
  { flag: '🇸🇬', name: 'Singapore', code: '+65' },
  { flag: '🇦🇪', name: 'UAE', code: '+971' },
  { flag: '🇮🇱', name: 'Israel', code: '+972' },
  { flag: '🇳🇱', name: 'Netherlands', code: '+31' },
  { flag: '🇨🇭', name: 'Switzerland', code: '+41' },
  { flag: '🌍', name: 'Other', code: '' },
];

const LEADUP_OPTIONS_FULL = [
  { key: 'twoWeeks', days: 14, label: '2 weeks before' },
  { key: 'sevenDays', days: 7,  label: '7 days before' },
  { key: 'threeDays', days: 3,  label: '3 days before' },
  { key: 'oneDay',   days: 1,  label: '1 day before' },
];

const LEADUP_OPTIONS_SHORT = [
  { key: 'threeDays', days: 3, label: '3 days before' },
  { key: 'oneDay',   days: 1, label: '1 day before' },
];

function getLeadUpOptions(occasion, frequency) {
  if (occasion === 'birthday') return LEADUP_OPTIONS_FULL;
  if (frequency === 'once' || frequency === 'annually') return LEADUP_OPTIONS_FULL;
  if (frequency === 'monthly') return LEADUP_OPTIONS_SHORT;
  return [];
}

function daysToLeadUpKey(days) {
  if (days === 14) return 'twoWeeks';
  if (days === 7)  return 'sevenDays';
  if (days === 3)  return 'threeDays';
  if (days === 1)  return 'oneDay';
  return null;
}

function detectTimezone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone; }
  catch { return 'America/New_York'; }
}

function findTimezoneMatch(tz) {
  const match = TIMEZONES.find(t => !t.group && t.value === tz);
  return match ? tz : 'America/New_York';
}

function formatTimezoneLabel(tzValue) {
  const found = TIMEZONES.find(t => !t.group && t.value === tzValue);
  return found ? found.label : tzValue;
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const s = {
  page: { minHeight: '100vh', background: '#FAFAF8', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 'env(safe-area-inset-top, 1.5rem) 1rem calc(env(safe-area-inset-bottom, 0px) + 4rem)' },
  card: { width: '100%', maxWidth: 480, background: '#fff', borderRadius: 16, border: '0.5px solid #e5e4e0', padding: '2rem 1.75rem', marginTop: '0' },
  heading: { fontFamily: "'DM Serif Display', serif", fontSize: 32, color: '#1a1a1a', marginBottom: 4 },
  sub: { fontSize: 14, color: '#888', marginBottom: '0.5rem' },
  tabs: { display: 'flex', border: '0.5px solid #e5e4e0', borderRadius: 10, overflow: 'hidden', marginBottom: '1.75rem' },
  tab: (a) => ({ flex: 1, padding: '9px 0', fontSize: 13, fontWeight: 500, textAlign: 'center', cursor: 'pointer', border: 'none', background: a ? '#1a1a1a' : '#fff', color: a ? '#fff' : '#888', transition: 'all 0.15s' }),
  label: { display: 'block', fontSize: 11, fontWeight: 500, color: '#888', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' },
  labelOptional: { display: 'block', fontSize: 11, fontWeight: 400, color: '#bbb', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' },
  input: { width: '100%', padding: '11px 13px', fontSize: 16, border: '0.5px solid #e5e4e0', borderRadius: 10, background: '#FAFAF8', color: '#1a1a1a', outline: 'none' },
  select: { width: '100%', padding: '11px 13px', fontSize: 16, border: '0.5px solid #e5e4e0', borderRadius: 10, background: '#FAFAF8', color: '#1a1a1a', outline: 'none' },
  phoneField: { display: 'flex', gap: 8 },
  countrySelect: { padding: '11px 8px', fontSize: 14, border: '0.5px solid #e5e4e0', borderRadius: 10, background: '#FAFAF8', color: '#1a1a1a', outline: 'none', flexShrink: 0, width: 80 },
  phoneHint: { fontSize: 12, color: '#aaa', marginTop: 4 },
  monthDayRow: { display: 'flex', gap: 8 },
  monthSelect: { flex: 2, padding: '11px 8px', fontSize: 16, border: '0.5px solid #e5e4e0', borderRadius: 10, background: '#FAFAF8', color: '#1a1a1a', outline: 'none' },
  daySelect: { flex: 1, padding: '11px 8px', fontSize: 16, border: '0.5px solid #e5e4e0', borderRadius: 10, background: '#FAFAF8', color: '#1a1a1a', outline: 'none' },
  textarea: { width: '100%', padding: '11px 13px', fontSize: 16, border: '0.5px solid #e5e4e0', borderRadius: 10, background: '#FAFAF8', color: '#1a1a1a', outline: 'none', resize: 'none', height: 90, lineHeight: 1.5 },
  field: { marginBottom: '1.25rem' },
  occasionGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 },
  occasionCard: (a) => ({ padding: '14px 12px', border: a ? '1.5px solid #1a1a1a' : '0.5px solid #e5e4e0', borderRadius: 12, background: a ? '#f5f5f3' : '#fff', cursor: 'pointer', textAlign: 'left', transition: 'all 0.1s' }),
  occasionIcon: { fontSize: 22, display: 'block', marginBottom: 8 },
  occasionTitle: { fontSize: 13, fontWeight: 500, color: '#1a1a1a' },
  occasionSub: { fontSize: 11, color: '#888', marginTop: 2 },
  rtypeRow: { display: 'flex', gap: 8 },
  rtype: (a) => ({ flex: 1, padding: '12px 8px', border: a ? '1.5px solid #1a1a1a' : '0.5px solid #e5e4e0', borderRadius: 12, background: a ? '#f5f5f3' : '#fff', cursor: 'pointer', textAlign: 'center', transition: 'all 0.1s' }),
  rtypeTitle: { fontSize: 14, fontWeight: 500, color: '#1a1a1a' },
  rtypeSub: { fontSize: 12, color: '#888', marginTop: 4 },
  toggleRow: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  tog: (a) => ({ flex: 1, padding: '11px 0', fontSize: 14, fontWeight: 500, border: a ? '1.5px solid #1a1a1a' : '0.5px solid #e5e4e0', borderRadius: 10, background: a ? '#1a1a1a' : '#fff', color: a ? '#fff' : '#888', cursor: 'pointer', transition: 'all 0.15s', minWidth: 80 }),
  togLocked: { width: '100%', padding: '11px 0', fontSize: 14, fontWeight: 500, border: '1.5px solid #e5e4e0', borderRadius: 10, background: '#f5f5f3', color: '#bbb', textAlign: 'center' },
  hint: { fontSize: 12, color: '#aaa', marginTop: 5 },
  presetMsg: { padding: '10px 12px', border: '0.5px solid #e5e4e0', borderRadius: 10, background: '#FAFAF8' },
  presetText: { fontSize: 13, color: '#1a1a1a', lineHeight: 1.4 },
  presetHint: { fontSize: 11, color: '#aaa', marginTop: 4 },
  leadUpBox: { background: '#FAFAF8', border: '0.5px solid #e5e4e0', borderRadius: 10, overflow: 'hidden' },
  leadUpRow: (checked, locked) => ({
    display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px',
    borderBottom: '0.5px solid #e5e4e0',
    background: locked ? '#f9f9f7' : checked ? '#f5f5f3' : '#FAFAF8',
    cursor: locked ? 'not-allowed' : 'pointer', transition: 'background 0.1s',
  }),
  leadUpRowLast: (checked, locked) => ({
    display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px',
    background: locked ? '#f9f9f7' : checked ? '#f5f5f3' : '#FAFAF8',
    cursor: locked ? 'not-allowed' : 'pointer', transition: 'background 0.1s',
  }),
  checkbox: (checked, locked) => ({
    width: 18, height: 18, borderRadius: 5,
    border: locked ? '1.5px solid #ddd' : checked ? '1.5px solid #1a1a1a' : '1.5px solid #ccc',
    background: locked ? '#eee' : checked ? '#1a1a1a' : '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0, transition: 'all 0.1s',
  }),
  checkmark: { color: '#fff', fontSize: 11, fontWeight: 700, lineHeight: 1 },
  leadUpLabel: (locked) => ({ fontSize: 13, color: locked ? '#bbb' : '#1a1a1a', flex: 1 }),
  leadUpMsg: { fontSize: 11, color: '#aaa', lineHeight: 1.4, flex: 2, textAlign: 'right' },
  previewBox: { background: '#FAFAF8', border: '0.5px solid #e5e4e0', borderRadius: 12, padding: '1rem 1.25rem', marginBottom: '1.25rem' },
  previewName: { fontSize: 17, fontWeight: 600, color: '#1a1a1a', marginBottom: 4 },
  previewMeta: { fontSize: 13, color: '#888', marginBottom: '1rem' },
  previewSectionLabel: { fontSize: 10, fontWeight: 600, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 },
  previewMsgRow: { display: 'flex', gap: 12, paddingTop: 8, paddingBottom: 8, borderBottom: '0.5px solid #f0efec' },
  previewMsgRowLast: { display: 'flex', gap: 12, paddingTop: 8, paddingBottom: 0 },
  previewMsgLabel: { fontSize: 12, color: '#aaa', width: 90, flexShrink: 0, paddingTop: 1 },
  previewMsgText: { fontSize: 12, color: '#1a1a1a', lineHeight: 1.5, flex: 1 },
  previewBtnRow: { display: 'flex', gap: 8, marginTop: 8 },
  editFormBtn: { flex: 1, padding: '12px 0', fontSize: 14, fontWeight: 500, border: '0.5px solid #e5e4e0', borderRadius: 10, background: '#fff', color: '#1a1a1a', cursor: 'pointer' },
  confirmBtn: { flex: 2, padding: '12px 0', fontSize: 14, fontWeight: 500, border: 'none', borderRadius: 10, background: '#1a1a1a', color: '#fff', cursor: 'pointer' },
  successBannerInline: { background: '#F0FAF0', border: '0.5px solid #a8d8a8', borderRadius: 10, padding: '1rem 1.25rem', marginBottom: 12 },
  successTitle: { fontSize: 14, fontWeight: 500, color: '#2d6a2d', marginBottom: 4 },
  successSub: { fontSize: 13, color: '#3d8a3d', marginBottom: 10 },
  vcardBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '8px 0', fontSize: 13, fontWeight: 500, border: '1px solid #a8d8a8', borderRadius: 8, background: '#fff', color: '#2d6a2d', cursor: 'pointer' },
  error: { background: '#FEF2F2', border: '0.5px solid #FCA5A5', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#991B1B', marginBottom: '1rem' },
  disclosure: { fontSize: 11, color: '#aaa', lineHeight: 1.6, marginTop: 12, marginBottom: 8, textAlign: 'center' },
  // Timezone confirmation note on preview card
  tzNote: { fontSize: 12, color: '#888', lineHeight: 1.6, marginTop: 10, marginBottom: 2, padding: '8px 12px', background: '#F5F5F3', borderRadius: 8 },
  submitBtn: { width: '100%', padding: '14px 0', fontSize: 16, fontWeight: 500, border: 'none', borderRadius: 10, background: '#1a1a1a', color: '#fff', cursor: 'pointer', marginTop: 8 },
  addAnotherBtn: { width: '100%', padding: '14px 0', fontSize: 16, fontWeight: 500, border: '0.5px solid #e5e4e0', borderRadius: 10, background: '#fff', color: '#1a1a1a', cursor: 'pointer', marginTop: 8 },
  editHeader: { background: '#F5F5F3', border: '0.5px solid #e5e4e0', borderRadius: 12, padding: '1rem 1.25rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  editHeaderTitle: { fontSize: 15, fontWeight: 600, color: '#1a1a1a', marginBottom: 2 },
  editHeaderSub: { fontSize: 13, color: '#888' },
  editCancelBtn: { fontSize: 12, color: '#aaa', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', flexShrink: 0 },
  tzCard: { background: '#FAFAF8', border: '0.5px solid #e5e4e0', borderRadius: 12, padding: '1.5rem 1.25rem', marginBottom: '1.25rem' },
  tzTitle: { fontSize: 17, fontWeight: 600, color: '#1a1a1a', marginBottom: 6 },
  tzSub: { fontSize: 13, color: '#888', lineHeight: 1.6, marginBottom: '1.25rem' },
  tzHint: { fontSize: 12, color: '#aaa', lineHeight: 1.6, marginTop: 10 },
  reminderCard: { border: '0.5px solid #e5e4e0', borderRadius: 12, padding: '1rem 1.25rem', marginBottom: 10, background: '#FAFAF8', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  reminderName: { fontSize: 15, fontWeight: 500, color: '#1a1a1a', marginBottom: 4 },
  reminderMeta: { fontSize: 12, color: '#888', lineHeight: 1.8 },
  badge: (color) => {
    const map = { purple: ['#EEEDFE','#3C3489'], amber: ['#FAEEDA','#633806'], green: ['#EAF3DE','#27500A'], blue: ['#E6F1FB','#0C447C'] };
    const [bg, fg] = map[color] || map.blue;
    return { display: 'inline-block', fontSize: 11, padding: '2px 8px', borderRadius: 99, marginRight: 4, background: bg, color: fg };
  },
  cardActions: { display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0, marginLeft: 8 },
  actionBtn: (danger) => ({ fontSize: 12, color: danger ? '#bbb' : '#888', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', textAlign: 'right' }),
  emptyState: { textAlign: 'center', padding: '2rem 0', color: '#aaa', fontSize: 14 },
  loadedHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' },
  sectionLabel: { fontSize: 11, fontWeight: 500, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 2 },
  changeBtn: { fontSize: 12, color: '#aaa', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' },
  tzRow: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 },
  tzLabel: { fontSize: 12, color: '#aaa' },
  tzChangeBtn: { fontSize: 12, color: '#888', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 },
  footer: { marginTop: '1.5rem', paddingTop: '1.25rem', borderTop: '0.5px solid #e5e4e0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
  footerText: { fontSize: 12, color: '#aaa', lineHeight: 1.5 },
  footerBtn: { flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', fontSize: 12, fontWeight: 500, border: '0.5px solid #e5e4e0', borderRadius: 8, background: '#fff', color: '#1a1a1a', cursor: 'pointer', whiteSpace: 'nowrap' },
  siteFooter: { width: '100%', maxWidth: 480, marginTop: '2rem', paddingTop: '1.5rem', borderTop: '0.5px solid #e5e4e0', textAlign: 'center' },
  siteFooterBrand: { fontFamily: "'DM Serif Display', serif", fontSize: 16, color: '#1a1a1a', marginBottom: 6 },
  siteFooterCopy: { fontSize: 12, color: '#aaa', marginBottom: 12 },
  siteFooterLinks: { display: 'flex', justifyContent: 'center', gap: 20 },
  siteFooterLink: { fontSize: 12, color: '#888', textDecoration: 'none' },
  phoneCenter: { textAlign: 'center', padding: '1rem 0 1.5rem' },
  lookupBtn: { padding: '11px 16px', fontSize: 16, fontWeight: 500, border: 'none', borderRadius: 10, background: '#1a1a1a', color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap' },
};

function downloadVcard() {
  const vcard = `BEGIN:VCARD\nVERSION:3.0\nFN:TextReminder\nTEL:${TWILIO_PHONE}\nEND:VCARD`;
  const blob = new Blob([vcard], { type: 'text/vcard' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = 'TextReminder.vcf'; a.click();
  URL.revokeObjectURL(url);
}

function authFetch(url, options = {}) {
  const token = localStorage.getItem('tr_token');
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  }).then(res => {
    if (res.status === 401) {
      localStorage.removeItem('tr_token');
      localStorage.removeItem('tr_phone');
      window.location.reload();
    }
    return res;
  });
}

function formatDate(dateStr) {
  const [, month, day] = dateStr.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[parseInt(month) - 1]} ${parseInt(day)}`;
}

function birthdayDayOfMessage(name) {
  return `Today is ${name}'s birthday — don't forget to reach out!`;
}

function getLeadUpMessage(name, occasion, days, eventTime) {
  const subject = occasion === 'birthday' ? `${name}'s birthday` : name;
  const inPhrase = occasion === 'birthday' ? 'is in' : 'in';
  if (days === 14) return `${subject} ${inPhrase} 2 weeks.`;
  if (days === 7)  return `${subject} ${inPhrase} 7 days.`;
  if (days === 3)  return `${subject} ${inPhrase} 3 days.`;
  if (days === 1) {
    const timeStr = eventTime ? ` at ${eventTime}` : '';
    return occasion === 'birthday' ? `${subject} is tomorrow.` : `${subject} tomorrow${timeStr}.`;
  }
  return occasion === 'birthday' ? `${subject} is coming up.` : `${subject} coming up.`;
}

function buildOtherDayOfMessage(name, eventTime, additionalNote) {
  const base = `${name} today${eventTime ? ` at ${eventTime}` : ''}.`;
  return additionalNote.trim() ? `${base} ${additionalNote.trim()}` : base;
}

function frequencyDisplayLabel(r) {
  if (r.occasion === 'birthday') return 'Birthday';
  if (r.annual || r.frequency === 'annually') return 'Annual';
  if (r.frequency === 'weekly') return 'Weekly';
  if (r.frequency === 'monthly') return 'Monthly';
  return 'One-time';
}

function reminderWhenLabel(r) {
  const date = r.date || r.group_date;
  if (r.occasion === 'birthday' || r.annual || r.frequency === 'annually') {
    if (!date) return '';
    const month = MONTHS[parseInt(date.slice(5, 7)) - 1];
    const day = parseInt(date.slice(8, 10));
    return `Every ${month} ${ordinal(day)}`;
  }
  if (r.frequency === 'once') {
    if (!date) return '';
    const month = MONTHS[parseInt(date.slice(5, 7)) - 1];
    const day = parseInt(date.slice(8, 10));
    const year = date.slice(0, 4);
    return `${month} ${ordinal(day)}, ${year}`;
  }
  if (r.frequency === 'weekly') {
    const day = DAYS_OF_WEEK.find(d => d.value === r.day_of_week);
    return day ? `Every ${day.label}` : '';
  }
  if (r.frequency === 'monthly') {
    if (r.day_of_month === -1) return 'Every last day of month';
    return `Every ${ordinal(r.day_of_month)}`;
  }
  return '';
}

function buildFrequencyLabel(occasion, frequency, dayOfWeek, dayOfMonth, date, birthMonth, birthDay) {
  if (occasion === 'birthday') {
    const m = MONTHS[parseInt(birthMonth) - 1];
    return `Every year · ${m} ${birthDay}`;
  }
  if (frequency === 'once') {
    const [year, month, day] = date.split('-');
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `One-time · ${months[parseInt(month) - 1]} ${parseInt(day)}, ${year}`;
  }
  if (frequency === 'annually') {
    const m = MONTHS[parseInt(date.slice(5, 7)) - 1];
    const d = parseInt(date.slice(8, 10));
    return `Every year · ${m} ${d}`;
  }
  if (frequency === 'weekly') {
    const day = DAYS_OF_WEEK.find(d => d.value === parseInt(dayOfWeek));
    return `Every ${day ? day.label : 'week'}`;
  }
  if (frequency === 'monthly') {
    if (parseInt(dayOfMonth) === -1) return 'Monthly · last day of month';
    return `Monthly · ${ordinal(parseInt(dayOfMonth))}`;
  }
  return '';
}

function buildPreviewMessages(name, occasion, frequency, reminderType, selectedLeadUps, weeklyHeadsUp, eventTime, additionalNote, leadUpOptions) {
  const messages = [];
  const isWeekly = occasion === 'other' && frequency === 'weekly';
  const hasLeadUp = isWeekly ? weeklyHeadsUp : reminderType === 'leadup' && selectedLeadUps.length > 0;

  if (hasLeadUp) {
    if (isWeekly) {
      messages.push({ label: '1 day before', text: getLeadUpMessage(name, occasion, 1, eventTime) });
    } else {
      const sorted = leadUpOptions.filter(o => selectedLeadUps.includes(o.key)).sort((a, b) => b.days - a.days);
      for (const opt of sorted) {
        messages.push({ label: opt.label, text: getLeadUpMessage(name, occasion, opt.days, occasion === 'other' ? eventTime : '') });
      }
    }
  }

  const dayOfMsg = occasion === 'birthday'
    ? birthdayDayOfMessage(name)
    : buildOtherDayOfMessage(name, eventTime, additionalNote);
  messages.push({ label: 'On the day', text: dayOfMsg });
  return messages;
}

// ─── SMS Consent (shown once after first OTP verification) ────────────────────
// ─── OTP Verify (inline step, reused in CreateScreen and ListScreen) ──────────
function OtpVerify({ phone, onComplete, onBack, autoSend = true }) {
  const [step, setStep] = useState(autoSend ? 'otp' : 'confirm'); // 'confirm' | 'otp'
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(0);
  const timerRef = useRef(null);
  const digitRefs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];

  useEffect(() => {
    if (autoSend) sendCode();
    return () => clearInterval(timerRef.current);
  }, []);

  async function sendCode() {
    setSending(true);
    setError('');
    try {
      const res = await fetch(`${API}/auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send code.');
      startCountdown();
      setTimeout(() => digitRefs[0].current?.focus(), 100);
    } catch (err) {
      setError(err.message);
    } finally {
      setStep('otp');
      setSending(false);
    }
  }

  function startCountdown() {
    setSecondsLeft(60);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setSecondsLeft(s => {
        if (s <= 1) { clearInterval(timerRef.current); return 0; }
        return s - 1;
      });
    }, 1000);
  }

  function handleDigitChange(i, val) {
    const cleaned = val.replace(/\D/g, '');
    if (cleaned.length > 1) {
      const next = cleaned.slice(0, 6).split('');
      const newDigits = [...digits];
      next.forEach((d, idx) => { if (idx < 6) newDigits[idx] = d; });
      setDigits(newDigits);
      digitRefs[Math.min(next.length - 1, 5)].current?.focus();
      return;
    }
    const newDigits = [...digits];
    newDigits[i] = cleaned;
    setDigits(newDigits);
    if (cleaned && i < 5) digitRefs[i + 1].current?.focus();
  }

  function handleDigitKeydown(e, i) {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      const newDigits = [...digits];
      newDigits[i - 1] = '';
      setDigits(newDigits);
      digitRefs[i - 1].current?.focus();
    }
    if (e.key === 'Enter') handleVerify();
  }

  async function handleVerify() {
    const code = digits.join('');
    if (code.length < 6) return;
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone, code }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Incorrect code — try again.');
      clearInterval(timerRef.current);
      onComplete(data.token, phone);
    } catch (err) {
      setError(err.message);
      setDigits(['', '', '', '', '', '']);
      setTimeout(() => digitRefs[0].current?.focus(), 50);
    } finally {
      setLoading(false);
    }
  }

  const otpComplete = digits.every(d => d !== '');
  const digitBox = (i) => ({
    width: 46, height: 56, fontSize: 24, fontWeight: 600,
    textAlign: 'center', border: digits[i] ? '1.5px solid #1a1a1a' : '0.5px solid #e5e4e0',
    borderRadius: 10, background: digits[i] ? '#fff' : '#FAFAF8',
    color: '#1a1a1a', outline: 'none', fontFamily: 'inherit', caretColor: 'transparent',
  });

  if (step === 'confirm') {
    return (
      <div>
        <div style={{ fontSize: 15, fontWeight: 600, color: '#1a1a1a', marginBottom: 4 }}>Verify your number</div>
        <div style={{ fontSize: 13, color: '#aaa', marginBottom: '1.25rem' }}>
          We'll send a verification code to <strong>{phone}</strong> to confirm it's you.
        </div>
        {error && <div style={s.error}>{error}</div>}
        <button style={s.submitBtn} onClick={sendCode} disabled={sending}>
          {sending ? 'Sending…' : 'Send verification code'}
        </button>
        {onBack && (
          <div style={{ textAlign: 'center', marginTop: 12 }}>
            <button style={{ ...s.changeBtn, fontSize: 13 }} onClick={onBack}>← Back</button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div style={{ fontSize: 15, fontWeight: 600, color: '#1a1a1a', marginBottom: 4 }}>Check your texts</div>
      <div style={{ fontSize: 13, color: '#aaa', marginBottom: '1rem' }}>
        {sending ? 'Sending code…' : <>We sent a 6-digit code to <strong>{phone}</strong>.</>}
      </div>
      {error && <div style={s.error}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', margin: '1.5rem 0' }}>
        {digits.map((d, i) => (
          <input
            key={i} ref={digitRefs[i]} style={digitBox(i)}
            type="tel" inputMode="numeric" maxLength={6} value={d}
            onChange={e => handleDigitChange(i, e.target.value)}
            onKeyDown={e => handleDigitKeydown(e, i)}
            onFocus={e => e.target.select()}
          />
        ))}
      </div>
      <button style={s.submitBtn} onClick={handleVerify} disabled={!otpComplete || loading || sending}>
        {loading ? 'Verifying…' : 'Verify'}
      </button>
      <div style={{ fontSize: 11, color: '#bbb', textAlign: 'center', marginTop: 14 }}>
        You'll stay logged in for 30 days on this device.
      </div>
      <div style={{ textAlign: 'center', marginTop: 12, fontSize: 13, color: '#aaa' }}>
        {secondsLeft > 0
          ? `Resend in ${secondsLeft}s`
          : sending ? null
          : <button style={{ ...s.changeBtn, fontSize: 13 }} onClick={() => { setDigits(['','','','','','']); sendCode(); }}>Resend code</button>
        }
      </div>
      {onBack && (
        <div style={{ textAlign: 'center', marginTop: 10 }}>
          <button style={{ ...s.changeBtn, fontSize: 13 }} onClick={onBack}>← Use a different number</button>
        </div>
      )}
    </div>
  );
}

// ─── Timezone Setup Screen ─────────────────────────────────────────────────────
function TimezoneSetup({ onComplete }) {
  const [selectedTz, setSelectedTz] = useState(() => findTimezoneMatch(detectTimezone()));
  const [loading, setLoading] = useState(false);

  async function handleConfirm() {
    setLoading(true);
    try {
      await authFetch(`${API}/user-preferences`, {
        method: 'POST',
        body: JSON.stringify({ timezone: selectedTz }),
      });
      onComplete(selectedTz);
    } catch {
      onComplete(selectedTz);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <div style={s.tzCard}>
        <div style={s.tzTitle}>When should we send your reminders?</div>
        <div style={s.tzSub}>
          All reminders are delivered at 9:00 AM in your local time. You can update your timezone anytime from the My Reminders tab.
        </div>
        <div style={s.field}>
          <label style={s.label}>Your timezone</label>
          <select style={s.select} value={selectedTz} onChange={e => setSelectedTz(e.target.value)}>
            {TIMEZONES.map((tz, i) =>
              tz.group
                ? <optgroup key={i} label={tz.label} />
                : <option key={tz.value} value={tz.value}>{tz.label}</option>
            )}
          </select>
        </div>
      </div>
      <button style={s.submitBtn} onClick={handleConfirm} disabled={loading}>
        {loading ? 'Saving...' : 'Confirm timezone'}
      </button>
    </div>
  );
}

// ─── Create Screen ─────────────────────────────────────────────────────────────
function CreateScreen({ loggedInPhone, onLogin, onSaved, previewing, setPreviewing, editingReminder, setEditingReminder, onTabChange, userTimezone, setUserTimezone, onOtpTransitioning }) {
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+1');
  const [createLoginStep, setCreateLoginStep] = useState('phone'); // 'phone' | 'otp'
  const [otpTransitioning, setOtpTransitioning] = useState(false);
  const [needsConsent, setNeedsConsent] = useState(() => !localStorage.getItem('tr_sms_consent'));
  const [smsConsent, setSmsConsent] = useState(false);
  useEffect(() => { if (previewing) setOtpTransitioning(false); }, [previewing]);

  const [occasion, setOccasion] = useState('birthday');
  const [name, setName] = useState('');
  const [date, setDate] = useState('');
  const [frequency, setFrequency] = useState('once');
  const [birthMonth, setBirthMonth] = useState('');
  const [birthDay, setBirthDay] = useState('');
  const [dayOfWeek, setDayOfWeek] = useState('');
  const [dayOfMonth, setDayOfMonth] = useState('');
  const [reminderType, setReminderType] = useState('dayof');
  const [selectedLeadUps, setSelectedLeadUps] = useState([]);
  const [weeklyHeadsUp, setWeeklyHeadsUp] = useState(false);
  const [eventTime, setEventTime] = useState('');
  const [additionalNote, setAdditionalNote] = useState('');
  const [channel, setChannel] = useState('sms');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const [showTimezoneSetup, setShowTimezoneSetup] = useState(false);
  const [timezoneJustSet, setTimezoneJustSet] = useState(false);
  const formTopRef = useRef(null);

  useEffect(() => {
    if (!editingReminder) return;
    const r = editingReminder;
    setOccasion(r.occasion || 'birthday');
    setName(r.name || '');
    setFrequency(r.frequency || 'once');
    setChannel(r.channel || 'sms');
    if (r.occasion === 'birthday' && r.date) {
      setBirthMonth(parseInt(r.date.slice(5, 7)).toString());
      setBirthDay(parseInt(r.date.slice(8, 10)).toString());
    } else {
      setDate(r.date ? r.date.slice(0, 10) : '');
    }
    if (r.day_of_week !== null && r.day_of_week !== undefined) setDayOfWeek(r.day_of_week.toString());
    if (r.day_of_month !== null && r.day_of_month !== undefined) setDayOfMonth(r.day_of_month.toString());
    setEventTime(r.event_time || '');
    setAdditionalNote(r.additional_note || '');
    const isLeadUp = r.display_type === 'leadup';
    setReminderType(isLeadUp ? 'leadup' : 'dayof');
    setWeeklyHeadsUp(false);
    if (isLeadUp) {
      authFetch(`${API}/reminder-group/${r.id}`)
        .then(res => res.json())
        .then(data => {
          if (data.leadUpDays) {
            const keys = data.leadUpDays.map(d => daysToLeadUpKey(d)).filter(Boolean);
            setSelectedLeadUps(keys);
            if (r.frequency === 'weekly' && data.leadUpDays.includes(1)) setWeeklyHeadsUp(true);
          }
        })
        .catch(() => setSelectedLeadUps([]));
    } else {
      setSelectedLeadUps([]);
    }
  }, [editingReminder]);

  const displayName = name.trim() || (occasion === 'birthday' ? 'John' :
    frequency === 'weekly' ? 'Call Grandma' :
    frequency === 'monthly' ? 'Rent payment' :
    frequency === 'annually' ? "Sarah's anniversary" :
    'Dentist appointment');

  const showReminderType = occasion === 'birthday' || frequency === 'once' || frequency === 'annually' || frequency === 'monthly';
  const isWeekly = occasion === 'other' && frequency === 'weekly';
  const leadUpOptions = getLeadUpOptions(occasion, frequency);
  const otherDayOfPreview = buildOtherDayOfMessage(name.trim() || displayName, eventTime, additionalNote);

  function toggleLeadUp(key) {
    setSelectedLeadUps(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  }

  function validate() {
    if (!loggedInPhone && !phone) { setError('Please fill in all fields.'); return false; }
    if (!name) { setError('Please fill in all fields.'); return false; }
    if (occasion === 'birthday' && (!birthMonth || !birthDay)) { setError('Please select a month and day.'); return false; }
    if (occasion === 'other' && frequency === 'once' && !date) { setError('Please select a date.'); return false; }
    if (occasion === 'other' && frequency === 'annually' && !date) { setError('Please select a date.'); return false; }
    if (occasion === 'other' && frequency === 'weekly' && dayOfWeek === '') { setError('Please select a day of the week.'); return false; }
    if (occasion === 'other' && frequency === 'monthly' && dayOfMonth === '') { setError('Please select a day of the month.'); return false; }
    if (occasion === 'other' && frequency === 'once') {
      const today = new Date().toISOString().slice(0, 10);
      if (date < today) { setError('Please choose a future date for one-time reminders.'); return false; }
    }
    return true;
  }

  function handleSaveClick() {
    setError('');
    if (!validate()) return;
    if (!userTimezone) {
      setShowTimezoneSetup(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setPreviewing(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function handleCreateSendCode() {
    setError('');
    if (!validate()) return;
    const fullPhone = countryCode + phone;
    if (fullPhone.replace(/\D/g, '').length < 7) { setError('Please enter your phone number.'); return; }
    setCreateLoginStep('otp');
  }

  async function handleOtpComplete(token, ph) {
    setOtpTransitioning(true);
    onOtpTransitioning(true);
    onLogin(token, ph);
    setCreateLoginStep('phone');
    try {
      const res = await authFetch(`${API}/user-preferences/${encodeURIComponent(ph)}`);
      const data = await res.json();
      if (data.preferences?.timezone) {
        setUserTimezone(data.preferences.timezone);
        setPreviewing(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    } catch {}
    if (!userTimezone) {
      setShowTimezoneSetup(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setPreviewing(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function handleTimezoneComplete(tz) {
    setUserTimezone(tz);
    setTimezoneJustSet(true);
    setShowTimezoneSetup(false);
    setPreviewing(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function handleConfirm() {
    if (needsConsent && !smsConsent) {
      setError('Please agree to receive text reminders to continue.');
      return;
    }
    const finalDate = occasion === 'birthday'
      ? `2026-${String(birthMonth).padStart(2, '0')}-${String(birthDay).padStart(2, '0')}`
      : (frequency === 'weekly' || frequency === 'monthly') ? new Date().toISOString().slice(0, 10)
      : date;

    const finalAnnual = occasion === 'birthday' ? true : frequency === 'annually';
    let finalReminderType = 'dayof';
    let leadUpDays = [];

    if (isWeekly && weeklyHeadsUp) {
      finalReminderType = 'leadup'; leadUpDays = [1];
    } else if (showReminderType && reminderType === 'leadup') {
      finalReminderType = 'leadup';
      leadUpDays = leadUpOptions.filter(o => selectedLeadUps.includes(o.key)).map(o => o.days);
    }

    const body = {
      name: name.trim(),
      date: finalDate,
      annual: finalAnnual,
      frequency,
      occasion,
      reminderType: finalReminderType,
      leadUpDays,
      message: occasion === 'other' ? buildOtherDayOfMessage(name.trim(), eventTime, additionalNote) : null,
      eventTime: occasion === 'other' ? eventTime || undefined : undefined,
      additionalNote: occasion === 'other' ? additionalNote || undefined : undefined,
      channel,
      dayOfWeek: frequency === 'weekly' ? parseInt(dayOfWeek) : undefined,
      dayOfMonth: frequency === 'monthly' ? parseInt(dayOfMonth) : undefined,
      timezone: userTimezone || undefined,
    };

    setLoading(true);
    try {
      let res;
      if (editingReminder) {
        res = await authFetch(`${API}/reminders/${editingReminder.id}`, {
          method: 'PUT', body: JSON.stringify(body),
        });
      } else {
        res = await authFetch(`${API}/reminders`, {
          method: 'POST', body: JSON.stringify(body),
        });
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Something went wrong.');

      setSavedSnapshot({
        name: name.trim(), occasion, frequency, date, birthMonth, birthDay,
        dayOfWeek, dayOfMonth, reminderType, selectedLeadUps, weeklyHeadsUp,
        eventTime, additionalNote, channel,
        leadUpOptions: getLeadUpOptions(occasion, frequency),
      });
      if (needsConsent) {
        localStorage.setItem('tr_sms_consent', '1');
        setNeedsConsent(false);
      }
      setSaved(true);
      onSaved();
      setName(''); setDate(''); setBirthMonth(''); setBirthDay('');
      setSelectedLeadUps([]); setWeeklyHeadsUp(false); setDayOfWeek(''); setDayOfMonth('');
      setEventTime(''); setAdditionalNote('');
      setEditingReminder(null);
    } catch (err) {
      setError(err.message);
      setPreviewing(false);
    } finally {
      setLoading(false);
    }
  }

  // ── Timezone setup screen ──
  if (showTimezoneSetup) {
    return (
      <div ref={formTopRef}>
        <TimezoneSetup onComplete={handleTimezoneComplete} />
      </div>
    );
  }

  // ── Preview screen ──
  if (previewing) {
    const snap = saved && savedSnapshot ? savedSnapshot : {
      name: name.trim(), occasion, frequency, date, birthMonth, birthDay,
      dayOfWeek, dayOfMonth, reminderType, selectedLeadUps, weeklyHeadsUp,
      eventTime, additionalNote, channel,
      leadUpOptions: getLeadUpOptions(occasion, frequency),
    };
    const messages = buildPreviewMessages(snap.name, snap.occasion, snap.frequency, snap.reminderType, snap.selectedLeadUps, snap.weeklyHeadsUp, snap.eventTime, snap.additionalNote, snap.leadUpOptions);
    const freqLabel = buildFrequencyLabel(snap.occasion, snap.frequency, snap.dayOfWeek, snap.dayOfMonth, snap.date, snap.birthMonth, snap.birthDay);
    const channelLabel = snap.channel === 'sms' ? 'SMS' : snap.channel === 'whatsapp' ? 'WhatsApp' : 'SMS & WhatsApp';

    return (
      <div ref={formTopRef}>
        <div style={{ fontSize: 15, fontWeight: 600, color: '#1a1a1a', marginBottom: 4 }}>
          {editingReminder && !saved ? 'Review your changes' : 'Review your reminder'}
        </div>
        <div style={{ fontSize: 13, color: '#aaa', marginBottom: '1.25rem' }}>Make sure everything looks right before saving.</div>

        <div style={s.previewBox}>
          <div style={s.previewName}>{snap.occasion === 'birthday' ? `${snap.name}'s Birthday` : snap.name}</div>
          <div style={s.previewMeta}>{freqLabel} · {channelLabel}</div>
          <div style={s.previewSectionLabel}>Messages you'll receive</div>
          {messages.map((m, i) => (
            <div key={i} style={i < messages.length - 1 ? s.previewMsgRow : s.previewMsgRowLast}>
              <div style={s.previewMsgLabel}>{m.label}</div>
              <div style={s.previewMsgText}>{m.text}</div>
            </div>
          ))}
        </div>

        {/* Show timezone confirmation only on the first save where timezone was just set */}
        {timezoneJustSet && !saved && userTimezone && (
          <div style={s.tzNote}>
            🕘 Reminders will be sent at 9:00 AM <strong>{formatTimezoneLabel(userTimezone)}</strong>. You can change this anytime in My Reminders.
          </div>
        )}

        {!saved && needsConsent && (
          <div
            style={{ display: 'flex', alignItems: 'flex-start', gap: 12, margin: '12px 0', padding: 14, border: smsConsent ? '1.5px solid #1a1a1a' : '0.5px solid #e5e4e0', borderRadius: 12, background: '#FAFAF8', cursor: 'pointer' }}
            onClick={() => setSmsConsent(c => !c)}
          >
            <div style={s.checkbox(smsConsent, false)}>
              {smsConsent && <span style={s.checkmark}>✓</span>}
            </div>
            <p style={{ fontSize: 13, color: '#1a1a1a', lineHeight: 1.6, margin: 0 }}>
              I agree to receive recurring text messages from TextReminder.net. Message frequency varies. Message and data rates may apply. Reply STOP to opt out. Reply HELP for help. Terms: textreminder.net/terms.html. Privacy: textreminder.net/privacy.html.
            </p>
          </div>
        )}

        {saved ? (
          <>
            <div style={s.successBannerInline}>
              <div style={s.successTitle}>✓ {editingReminder ? 'Changes saved!' : 'Reminder saved!'}</div>
              <div style={s.successSub}>Save our number so you'll always recognize your reminders.</div>
              <button style={s.vcardBtn} onClick={downloadVcard}>📇 Save to Contacts</button>
            </div>
            <button style={s.addAnotherBtn} onClick={() => {
              setSaved(false);
              setSavedSnapshot(null);
              setPreviewing(false);
              setTimezoneJustSet(false);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}>
              Add another reminder
            </button>
          </>
        ) : (
          <>
            {loggedInPhone && (
              <div style={{ ...s.field, marginTop: 8 }}>
                <label style={s.label}>Your phone number</label>
                <div style={{ ...s.input, background: '#f5f5f3', color: '#888', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{loggedInPhone}</span>
                  <span style={{ fontSize: 11, color: '#bbb' }}>Verified</span>
                </div>
              </div>
            )}
            {error && <div style={s.error}>{error}</div>}
            <div style={s.previewBtnRow}>
              <button style={s.editFormBtn} onClick={() => { setPreviewing(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }} disabled={loading}>← Edit</button>
              <button style={s.confirmBtn} onClick={handleConfirm} disabled={loading}>
                {loading ? 'Saving...' : 'Confirm & save'}
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  // ── Form screen ──
  if (otpTransitioning) return null;

  return (
    <div ref={formTopRef}>
      {editingReminder && (
        <div style={s.editHeader}>
          <div>
            <div style={s.editHeaderTitle}>Edit reminder</div>
            <div style={s.editHeaderSub}>Make your changes below, then save.</div>
          </div>
          <button style={s.editCancelBtn} onClick={() => { setEditingReminder(null); onTabChange('list'); }}>Cancel</button>
        </div>
      )}
      <div style={s.field}>
        <label style={s.label}>Occasion</label>
        <div style={s.occasionGrid}>
          <div style={s.occasionCard(occasion === 'birthday')} onClick={() => { setOccasion('birthday'); setFrequency('once'); setReminderType('dayof'); setSelectedLeadUps([]); setWeeklyHeadsUp(false); setEventTime(''); setAdditionalNote(''); }}>
            <span style={s.occasionIcon}>🎂</span>
            <div style={s.occasionTitle}>Birthday</div>
            <div style={s.occasionSub}>Birthday reminders</div>
          </div>
          <div style={s.occasionCard(occasion === 'other')} onClick={() => { setOccasion('other'); setFrequency('once'); setReminderType('dayof'); setSelectedLeadUps([]); setWeeklyHeadsUp(false); }}>
            <span style={s.occasionIcon}>📅</span>
            <div style={s.occasionTitle}>Other</div>
            <div style={s.occasionSub}>Custom reminders</div>
          </div>
        </div>
      </div>

      <div style={s.field}>
        <label style={s.label}>{occasion === 'birthday' ? 'Name' : 'Reminder name'}</label>
        <input style={s.input} type="text"
          placeholder={occasion === 'birthday' ? 'e.g. John' :
            frequency === 'weekly' ? 'e.g. Call Grandma' :
            frequency === 'monthly' ? 'e.g. Rent payment' :
            frequency === 'annually' ? "e.g. Sarah's anniversary" :
            'e.g. Dentist appointment'}
          value={name} onChange={e => setName(e.target.value)} />
      </div>

      {occasion === 'birthday' && (
        <div style={s.field}>
          <label style={s.label}>Date</label>
          <div style={s.monthDayRow}>
            <select style={s.monthSelect} value={birthMonth} onChange={e => setBirthMonth(e.target.value)}>
              <option value="">Month</option>
              {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
            <select style={s.daySelect} value={birthDay} onChange={e => setBirthDay(e.target.value)}>
              <option value="">Day</option>
              {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
        </div>
      )}

      <div style={s.field}>
        <label style={s.label}>Frequency</label>
        {occasion === 'birthday' ? (
          <>
            <div style={s.togLocked}>Every year</div>
            <div style={s.hint}>Birthdays repeat every year automatically</div>
          </>
        ) : (
          <div style={s.toggleRow}>
            {[
              { key: 'once', label: 'One-time' },
              { key: 'weekly', label: 'Weekly' },
              { key: 'monthly', label: 'Monthly' },
              { key: 'annually', label: 'Every year' },
            ].map(f => (
              <button key={f.key} style={s.tog(frequency === f.key)} onClick={() => { setFrequency(f.key); setDayOfWeek(''); setDayOfMonth(''); setDate(''); setReminderType('dayof'); setSelectedLeadUps([]); setWeeklyHeadsUp(false); }}>
                {f.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {occasion === 'other' && (frequency === 'once' || frequency === 'annually') && (
        <div style={s.field}>
          <label style={s.label}>{frequency === 'annually' ? 'Month & Day' : 'Date'}</label>
          {frequency === 'annually' ? (
            <div style={s.monthDayRow}>
              <select style={s.monthSelect} value={date ? date.slice(5, 7) : ''} onChange={e => {
                const month = e.target.value;
                const day = date ? date.slice(8, 10) : '01';
                setDate(`2026-${month}-${day}`);
              }}>
                <option value="">Month</option>
                {MONTHS.map((m, i) => <option key={m} value={String(i + 1).padStart(2, '0')}>{m}</option>)}
              </select>
              <select style={s.daySelect} value={date ? date.slice(8, 10) : ''} onChange={e => {
                const day = e.target.value;
                const month = date ? date.slice(5, 7) : '01';
                setDate(`2026-${month}-${day}`);
              }}>
                <option value="">Day</option>
                {DAYS.map(d => <option key={d} value={String(d).padStart(2, '0')}>{d}</option>)}
              </select>
            </div>
          ) : (
            <input style={s.input} type="date" value={date} onChange={e => setDate(e.target.value)} />
          )}
        </div>
      )}

      {occasion === 'other' && frequency === 'weekly' && (
        <div style={s.field}>
          <label style={s.label}>Every</label>
          <select style={s.select} value={dayOfWeek} onChange={e => setDayOfWeek(e.target.value)}>
            <option value="">Select a day</option>
            {DAYS_OF_WEEK.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
        </div>
      )}

      {occasion === 'other' && frequency === 'monthly' && (
        <div style={s.field}>
          <label style={s.label}>Every month on the</label>
          <select style={s.select} value={dayOfMonth} onChange={e => setDayOfMonth(e.target.value)}>
            <option value="">Select a day</option>
            {DAYS_OF_MONTH.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
        </div>
      )}

      {isWeekly && (
        <div style={s.field}>
          <label style={s.label}>1-day heads up?</label>
          <div style={s.toggleRow}>
            <button style={s.tog(!weeklyHeadsUp)} onClick={() => setWeeklyHeadsUp(false)}>No</button>
            <button style={s.tog(weeklyHeadsUp)} onClick={() => setWeeklyHeadsUp(true)}>Yes</button>
          </div>
          <div style={s.hint}>Get a heads-up the day before your event each week</div>
          {weeklyHeadsUp && (
            <div style={{ ...s.leadUpBox, marginTop: 12 }}>
              <div style={s.leadUpRowLast(false, true)}>
                <div style={s.checkbox(true, true)}><span style={{ ...s.checkmark, color: '#bbb' }}>✓</span></div>
                <div style={s.leadUpLabel(false)}>1 day before</div>
                <div style={s.leadUpMsg}>{getLeadUpMessage(displayName, occasion, 1, eventTime)}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {showReminderType && (
        <div style={s.field}>
          <label style={s.label}>Reminder type</label>
          <div style={s.rtypeRow}>
            <div style={s.rtype(reminderType === 'dayof')} onClick={() => setReminderType('dayof')}>
              <div style={s.rtypeTitle}>Day-of only</div>
              <div style={s.rtypeSub}>1 text on the day</div>
            </div>
            <div style={s.rtype(reminderType === 'leadup')} onClick={() => setReminderType('leadup')}>
              <div style={s.rtypeTitle}>With lead-up reminders</div>
              <div style={s.rtypeSub}>Choose your schedule</div>
            </div>
          </div>
        </div>
      )}

      {occasion === 'birthday' && reminderType === 'dayof' && (
        <div style={s.field}>
          <label style={s.label}>Reminder message</label>
          <div style={s.presetMsg}>
            <div style={s.presetText}>{birthdayDayOfMessage(displayName)}</div>
            <div style={s.presetHint}>Sent on the day</div>
          </div>
        </div>
      )}

      {reminderType === 'leadup' && showReminderType && (
        <div style={s.field}>
          <label style={s.label}>Select your lead-up reminders</label>
          <div style={s.leadUpBox}>
            {leadUpOptions.map((opt, i) => {
              const checked = selectedLeadUps.includes(opt.key);
              const isLast = i === leadUpOptions.length - 1;
              const rowStyle = isLast ? s.leadUpRowLast(checked, false) : s.leadUpRow(checked, false);
              return (
                <div key={opt.key} style={rowStyle} onClick={() => toggleLeadUp(opt.key)}>
                  <div style={s.checkbox(checked, false)}>{checked && <span style={s.checkmark}>✓</span>}</div>
                  <div style={s.leadUpLabel(false)}>{opt.label}</div>
                  <div style={s.leadUpMsg}>{getLeadUpMessage(displayName, occasion, opt.days, occasion === 'other' ? eventTime : '')}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {reminderType === 'leadup' && showReminderType && occasion === 'birthday' && (
        <div style={s.field}>
          <label style={s.label}>Reminder message</label>
          <div style={s.presetMsg}>
            <div style={s.presetText}>{birthdayDayOfMessage(displayName)}</div>
            <div style={s.presetHint}>Sent on the day</div>
          </div>
        </div>
      )}

      {occasion === 'other' && (
        <>
          <div style={s.field}>
            <label style={s.label}>Reminder message</label>
            <div style={s.presetMsg}>
              <div style={s.presetText}>{otherDayOfPreview}</div>
              <div style={s.presetHint}>Sent on the day — based on your reminder name</div>
            </div>
          </div>
          <div style={s.field}>
            <label style={s.labelOptional}>Event time — optional</label>
            <select style={s.select} value={eventTime} onChange={e => setEventTime(e.target.value)}>
              <option value="">No time</option>
              {EVENT_TIMES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <div style={s.hint}>We'll include this in your reminder message</div>
          </div>
          <div style={s.field}>
            <label style={s.labelOptional}>Add a note — optional</label>
            <textarea style={s.textarea}
              placeholder={
                frequency === 'weekly' ? "e.g. Ask about her garden." :
                frequency === 'monthly' ? "e.g. Check that payment cleared." :
                frequency === 'annually' ? "e.g. Pick up a card on the way home." :
                "e.g. Don't forget to bring your insurance card."
              }
              value={additionalNote} onChange={e => setAdditionalNote(e.target.value)} />
          </div>
        </>
      )}

      {loggedInPhone && (
        <div style={s.field}>
          <label style={s.label}>Your phone number</label>
          <div style={{ ...s.input, background: '#f5f5f3', color: '#888', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{loggedInPhone}</span>
            <span style={{ fontSize: 11, color: '#bbb' }}>Verified</span>
          </div>
        </div>
      )}

      <div style={s.field}>
        <label style={s.label}>Send via</label>
        <div style={s.toggleRow}>
          {['sms','whatsapp','both'].map(c => (
            <button key={c} style={s.tog(channel === c)} onClick={() => setChannel(c)}>
              {c === 'sms' ? 'SMS' : c === 'whatsapp' ? 'WhatsApp' : 'Both'}
            </button>
          ))}
        </div>
      </div>

      {loggedInPhone ? (
        <>
          {error && <div style={{ ...s.error, marginBottom: 0, marginTop: '1rem' }}>{error}</div>}
          <button style={s.submitBtn} onClick={handleSaveClick} disabled={loading}>
            {editingReminder ? 'Save changes' : 'Save reminder'}
          </button>
        </>
      ) : createLoginStep === 'otp' ? (
        <OtpVerify
          phone={countryCode + phone}
          autoSend={true}
          onComplete={handleOtpComplete}
          onBack={() => setCreateLoginStep('phone')}
        />
      ) : (
        <>
          <div style={{ fontSize: 15, fontWeight: 500, color: '#1a1a1a', marginTop: 8, marginBottom: 6 }}>Create your account</div>
          <p style={{ fontSize: 13, color: '#aaa', marginBottom: '1.25rem' }}>We'll save your reminders so you can manage them at any time.</p>
          <div style={s.field}>
            <div style={s.phoneField}>
              <select style={s.countrySelect} value={countryCode} onChange={e => setCountryCode(e.target.value)}>
                {COUNTRY_CODES.map(c => <option key={c.name} value={c.code}>{c.flag} {c.code}</option>)}
              </select>
              <input style={{ ...s.input, flex: 1 }} type="tel"
                placeholder={countryCode ? '(214) 555-0000' : '+33 1 00 00 00 00'}
                value={phone} onChange={e => setPhone(e.target.value)} />
            </div>
            {!countryCode && <div style={s.phoneHint}>Include your country code e.g. +33</div>}
          </div>
          {error && <div style={{ ...s.error, marginBottom: 0, marginTop: '0.5rem' }}>{error}</div>}
          <button style={s.submitBtn} onClick={handleCreateSendCode}
            disabled={phone.replace(/\D/g, '').length < 7}>
            Send verification code
          </button>
        </>
      )}
    </div>
  );
}

// ─── List Screen ───────────────────────────────────────────────────────────────
function ListScreen({ loggedInPhone, onLogin, onEdit, userTimezone, onChangeTimezone, onTimezoneLoaded, onLogout }) {
  const [listPhone, setListPhone] = useState('');
  const [listCountryCode, setListCountryCode] = useState('+1');
  const [listStep, setListStep] = useState('phone'); // 'phone' | 'otp'
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (loggedInPhone) fetchReminders();
    else setLoading(false);
  }, [loggedInPhone]);

  async function fetchReminders() {
    setError(''); setLoading(true);
    try {
      const [remRes, prefRes] = await Promise.all([
        authFetch(`${API}/reminders/${encodeURIComponent(loggedInPhone)}`, { headers: { 'Cache-Control': 'no-cache' } }),
        authFetch(`${API}/user-preferences/${encodeURIComponent(loggedInPhone)}`, { headers: { 'Cache-Control': 'no-cache' } }),
      ]);
      const [remData, prefData] = await Promise.all([remRes.json(), prefRes.json()]);
      if (!remRes.ok) throw new Error(remData.error || 'Failed to load reminders.');
      setReminders(remData.reminders);
      if (prefData.preferences?.timezone) onTimezoneLoaded(prefData.preferences.timezone);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  async function deleteReminder(id) {
    try {
      await authFetch(`${API}/reminders/${id}`, { method: 'DELETE' });
      setReminders(prev => prev.filter(r => r.id !== id));
    } catch { setError('Failed to delete reminder.'); }
  }

  if (!loggedInPhone) {
    if (listStep === 'otp') {
      return (
        <OtpVerify
          phone={listCountryCode + listPhone}
          onComplete={(token, ph) => { onLogin(token, ph); setListStep('phone'); }}
          onBack={() => setListStep('phone')}
        />
      );
    }
    return (
      <div>
        <div style={s.phoneCenter}>
          <p style={{ fontSize: 15, fontWeight: 500, marginBottom: 6 }}>Log in to view your reminders</p>
          <p style={{ fontSize: 13, color: '#aaa', marginBottom: '1.25rem' }}>We'll send a verification code to confirm it's you.</p>
        </div>
        <div style={s.field}>
          <div style={s.phoneField}>
            <select style={s.countrySelect} value={listCountryCode} onChange={e => setListCountryCode(e.target.value)}>
              {COUNTRY_CODES.map(c => <option key={c.name} value={c.code}>{c.flag} {c.code}</option>)}
            </select>
            <input style={{ ...s.input, flex: 1 }} type="tel"
              placeholder={listCountryCode ? '(214) 555-0000' : '+33 1 00 00 00 00'}
              value={listPhone} onChange={e => setListPhone(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && listPhone.replace(/\D/g, '').length >= 7 && setListStep('otp')} />
          </div>
          {!listCountryCode && <div style={s.phoneHint}>Include your country code e.g. +33</div>}
        </div>
        <button style={{ ...s.lookupBtn, width: '100%', marginTop: 8 }}
          onClick={() => setListStep('otp')}
          disabled={listPhone.replace(/\D/g, '').length < 7}>
          Send verification code
        </button>
      </div>
    );
  }

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '2rem 0', color: '#aaa', fontSize: 14 }}>Loading…</div>;
  }

  return (
    <div>
      {error && <div style={s.error}>{error}</div>}
      <div style={s.loadedHeader}>
        <div>
          <div style={s.sectionLabel}>{reminders.length} active reminder{reminders.length !== 1 ? 's' : ''}</div>
          <div style={{ fontSize: 13, color: '#aaa' }}>{loggedInPhone}</div>
          {userTimezone && (
            <div style={s.tzRow}>
              <span style={s.tzLabel}>🕘 {formatTimezoneLabel(userTimezone)}</span>
              <button style={s.tzChangeBtn} onClick={onChangeTimezone}>Change</button>
            </div>
          )}
        </div>
        <button style={s.changeBtn} onClick={onLogout}>Log out</button>
      </div>

      {reminders.length === 0 && <div style={s.emptyState}>No active reminders yet.</div>}

      {reminders.map(r => {
        const whenLabel = reminderWhenLabel(r);
        const cardMessage = r.display_type === 'leadup'
          ? (r.dayof_message || r.message)
          : r.message;
        return (
        <div key={r.id} style={s.reminderCard}>
          <div style={{ flex: 1 }}>
            <div style={s.reminderName}>
              {r.occasion === 'birthday' ? `${r.name}'s Birthday` : r.name}
            </div>
            <div style={s.reminderMeta}>
              <span style={s.badge('purple')}>{frequencyDisplayLabel(r)}</span>
              {r.display_type === 'gift' || r.display_type === 'leadup'
                ? <span style={s.badge('amber')}>With lead-up</span>
                : <span style={s.badge('blue')}>Day-of</span>
              }
              {r.channel === 'both' ? (
                <><span style={s.badge('green')}>SMS</span><span style={s.badge('green')}>WhatsApp</span></>
              ) : (
                <span style={s.badge('green')}>{r.channel === 'whatsapp' ? 'WhatsApp' : 'SMS'}</span>
              )}
            </div>
            {whenLabel ? <div style={{ fontSize: 12, color: '#888', marginTop: 5 }}>{whenLabel}</div> : null}
            {cardMessage ? <div style={{ fontSize: 12, color: '#aaa', marginTop: 3, fontStyle: 'italic' }}>{cardMessage}</div> : null}
          </div>
          <div style={s.cardActions}>
            <button style={s.actionBtn(false)} onClick={() => onEdit(r)}>Edit</button>
            <button style={s.actionBtn(true)} onClick={() => deleteReminder(r.id)}>Delete</button>
          </div>
        </div>
        );
      })}

      <div style={s.footer}>
        <div style={s.footerText}>Save our number so you always recognize your reminders.</div>
        <button style={s.footerBtn} onClick={downloadVcard}>📇 Save to Contacts</button>
      </div>
    </div>
  );
}

// ─── App Root ──────────────────────────────────────────────────────────────────
export default function App() {
  const [loggedInPhone, setLoggedInPhone] = useState(() => localStorage.getItem('tr_phone') || null);
  const [tab, setTab] = useState('create');
  const [previewing, setPreviewing] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);
  const [userTimezone, setUserTimezone] = useState(null);
  const [showChangeTimezone, setShowChangeTimezone] = useState(false);
  const [otpTransitioning, setOtpTransitioning] = useState(false);
  useEffect(() => { if (previewing) setOtpTransitioning(false); }, [previewing]);

  function handleLogin(token, phone) {
    localStorage.setItem('tr_token', token);
    localStorage.setItem('tr_phone', phone);
    setLoggedInPhone(phone);
  }

  function handleLogout() {
    localStorage.removeItem('tr_token');
    localStorage.removeItem('tr_phone');
    setLoggedInPhone(null);
    setTab('create');
    setPreviewing(false);
    setEditingReminder(null);
    setUserTimezone(null);
  }

  function handleEdit(reminder) {
    setEditingReminder(reminder);
    setTab('create');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  if (showChangeTimezone) {
    return (
      <div style={s.page}>
        <div style={{ width: '100%', maxWidth: 480 }}>
          <h1 style={s.heading}>Remind me.</h1>
          <p style={s.sub}>Send yourself scheduled text reminders for birthdays, appointments, and more.</p>
        </div>
        <div style={s.card}>
          <TimezoneSetup onComplete={(tz) => { setUserTimezone(tz); setShowChangeTimezone(false); }} />
        </div>
        <div style={s.siteFooter}>
          <div style={s.siteFooterBrand}>TextReminder</div>
          <div style={s.siteFooterCopy}>© 2026 All rights reserved.</div>
          <div style={s.siteFooterLinks}>
            <a style={s.siteFooterLink} href="/privacy.html">Privacy</a>
            <a style={s.siteFooterLink} href="/terms.html">Terms</a>
            <a style={s.siteFooterLink} href="mailto:hello@textreminder.net">Contact</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={{ width: '100%', maxWidth: 480 }}>
        <h1 style={s.heading}>Remind me.</h1>
        <p style={s.sub}>Send yourself scheduled text reminders for birthdays, appointments, and more.</p>
      </div>
      <div style={s.card}>
        {!previewing && !editingReminder && !otpTransitioning && (
          <div style={s.tabs}>
            <button style={s.tab(tab === 'create')} onClick={() => { setTab('create'); setEditingReminder(null); }}>New reminder</button>
            <button style={s.tab(tab === 'list')} onClick={() => setTab('list')}>My reminders</button>
          </div>
        )}
        {tab === 'create'
          ? <CreateScreen
              loggedInPhone={loggedInPhone}
              onLogin={handleLogin}
              onSaved={() => {}}
              previewing={previewing}
              setPreviewing={setPreviewing}
              editingReminder={editingReminder}
              setEditingReminder={setEditingReminder}
              onTabChange={setTab}
              userTimezone={userTimezone}
              setUserTimezone={setUserTimezone}
              onOtpTransitioning={setOtpTransitioning}
            />
          : <ListScreen
              loggedInPhone={loggedInPhone}
              onLogin={handleLogin}
              onEdit={handleEdit}
              userTimezone={userTimezone}
              onChangeTimezone={() => setShowChangeTimezone(true)}
              onTimezoneLoaded={tz => setUserTimezone(tz)}
              onLogout={handleLogout}
            />
        }
      </div>
      <div style={s.siteFooter}>
        <div style={s.siteFooterBrand}>TextReminder</div>
        <div style={s.siteFooterCopy}>© 2026 All rights reserved.</div>
        <div style={s.siteFooterLinks}>
          <a style={s.siteFooterLink} href="/privacy.html">Privacy</a>
          <a style={s.siteFooterLink} href="/terms.html">Terms</a>
          <a style={s.siteFooterLink} href="mailto:hello@textreminder.net">Contact</a>
        </div>
      </div>
    </div>
  );
}
